import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { CalendarClock, Loader2, ArrowRight } from 'lucide-react';

const STATUS_LABEL = {
  requested: 'New', scheduled: 'Scheduled', shot: 'Shot', editing: 'Editing',
  delivered: 'Delivered', paid: 'Paid', canceled: 'Canceled', received: 'Received',
};
const INVOICE_STYLE = {
  paid: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  sent: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  draft: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
};

// Shoot times are stored as Phoenix wall-clock time (no offset); Phoenix is UTC-7 year-round.
const asPhoenix = (iso) => (/(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}-07:00`);
const fmtShoot = (iso) => (iso ? new Date(asPhoenix(iso)).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Phoenix',
}) : null);

const orderPath = (o) => (['real_estate', 'vacant_land'].includes(o.service_type) ? `/admin/real-estate-orders/${o.id}` : `/admin/orders/${o.id}`);

const OrderRow = ({ o }) => (
  <Link to={orderPath(o)} data-testid={`dashboard-order-${o.id}`}
    className="group flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 rounded-lg bg-[#050816]/40 hover:bg-[#050816]/80 border border-transparent hover:border-[#C6A14A]/30 transition-colors">
    <div className="flex-1 min-w-0">
      <p className="text-white truncate group-hover:text-[#C6A14A] transition-colors">
        {o.service_type === 'vacant_land' && <span className="text-[10px] uppercase tracking-wider text-emerald-300 border border-emerald-500/30 rounded px-1.5 py-0.5 mr-2 align-middle">Vacant Land</span>}
        {o.address}
      </p>
      <p className="text-xs text-[#E8E4DE]/50 truncate">{o.client_name || o.client_email || 'Unknown client'}</p>
    </div>
    <div className="flex items-center gap-2 flex-wrap text-xs">
      {o.scheduled_start ? (
        <span className={`inline-flex items-center gap-1 ${o.upcoming ? 'text-[#C6A14A]' : 'text-[#E8E4DE]/50'}`}>
          <CalendarClock className="h-3.5 w-3.5" />{fmtShoot(o.scheduled_start)}
        </span>
      ) : <span className="text-[#E8E4DE]/30">Not scheduled</span>}
      <Badge variant="outline" className="border-white/15 text-[#E8E4DE]/80">{STATUS_LABEL[o.status] || o.status}</Badge>
      <Badge variant="outline" className={INVOICE_STYLE[o.invoice_status] || 'border-white/10 text-[#E8E4DE]/40'}>
        {o.invoice_status ? `Invoice ${o.invoice_status}` : 'No invoice'}
      </Badge>
      <ArrowRight className="h-4 w-4 text-[#C6A14A] opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block" />
    </div>
  </Link>
);

export const DashboardOrders = ({ orders, loading }) => {
  const upcoming = orders.filter((o) => o.upcoming);
  const recent = orders.filter((o) => !o.upcoming);
  return (
    <section className="rounded-2xl border border-white/10 bg-[#0f1724] p-5" data-testid="dashboard-orders">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg text-white font-light tracking-wide">Orders</h2>
        <Link to="/admin/real-estate-orders?service=all" className="text-sm text-[#C6A14A] hover:underline" data-testid="dashboard-all-orders-link">View all</Link>
      </div>
      {loading ? <Loader2 className="h-6 w-6 text-[#C6A14A] animate-spin mx-auto my-8" /> : (
        <div className="space-y-5">
          {upcoming.length > 0 && (
            <div className="space-y-2" data-testid="dashboard-upcoming-orders">
              <p className="text-xs uppercase tracking-[0.15em] text-[#C6A14A]">Upcoming shoots</p>
              {upcoming.map((o) => <OrderRow key={o.id} o={o} />)}
            </div>
          )}
          <div className="space-y-2" data-testid="dashboard-recent-orders">
            <p className="text-xs uppercase tracking-[0.15em] text-[#E8E4DE]/50">Newest orders</p>
            {recent.length ? recent.map((o) => <OrderRow key={o.id} o={o} />)
              : <p className="text-sm text-[#E8E4DE]/40 py-4">No orders yet</p>}
          </div>
        </div>
      )}
    </section>
  );
};
